import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EvStationIcon from '@mui/icons-material/EvStation';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsIcon from '@mui/icons-material/Directions';
import BoltIcon from '@mui/icons-material/Bolt';
import { useTranslation } from 'react-i18next';

import { StationsLocationsTypes } from './types';
import {
  getMarkerColor,
  getAvailableCount,
  getConnectorStatusColor,
  MARKER_COLOR_MAP,
} from './markerUtils';

interface StationPreviewSheetProps {
  station: StationsLocationsTypes | null;
  onClose: () => void;
  onGoToStation: (station: StationsLocationsTypes) => void;
}

export const StationPreviewSheet = ({
  station,
  onClose,
  onGoToStation,
}: StationPreviewSheetProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  if (!station) return null;

  const markerColor =
    MARKER_COLOR_MAP[getMarkerColor(station.connectors, station.status)];
  const total = station.connectors?.length ?? 0;
  const available = getAvailableCount(station.connectors);
  const hasPricing = station.price != null;

  const hours = station.aroundTheClock
    ? '24/7'
    : station.startWorkingTime && station.endWorkingTime
      ? `${station.startWorkingTime} – ${station.endWorkingTime}`
      : null;

  const navigateUrl = `https://www.google.com/maps/dir/?api=1&destination=${station.latitude},${station.longitude}`;

  const containerSx = isDesktop
    ? {
        position: 'absolute' as const,
        top: 80,
        right: 16,
        bottom: 80,
        width: 380,
        zIndex: 6,
        borderRadius: '20px',
      }
    : {
        position: 'absolute' as const,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 6,
        borderRadius: '20px 20px 0 0',
        boxShadow: '0 -8px 32px rgba(0,0,0,0.5)',
      };

  return (
    <Box
      sx={{
        ...containerSx,
        backgroundColor: 'rgba(28,28,28,0.96)',
        backdropFilter: 'blur(16px)',
        border: '1px solid',
        borderColor: 'grey.800',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        animation: isDesktop ? 'fadeIn 0.2s ease' : 'slideUp 0.25s ease',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes slideUp': {
          from: { transform: 'translateY(100%)' },
          to: { transform: 'translateY(0)' },
        },
      }}
    >
      {!isDesktop && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: 0.5 }}>
          <Box
            sx={{
              width: 36,
              height: 4,
              borderRadius: 2,
              backgroundColor: 'grey.700',
            }}
          />
        </Box>
      )}

      <Stack sx={{ p: 2.5, gap: 2, overflow: 'hidden', flex: 1, minHeight: 0 }}>
        <Stack direction="row" alignItems="flex-start" gap={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              backgroundColor: `${markerColor}22`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <EvStationIcon sx={{ color: markerColor, fontSize: 22 }} />
          </Box>
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              color="common.white"
              sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.25 }}
            >
              {station.address}
            </Typography>
            {station.code != null && (
              <Typography variant="caption" color="grey.400">
                {t('stationPageWithSearch.stationCode')}: #{station.code}
              </Typography>
            )}
          </Stack>
          <IconButton onClick={onClose} sx={{ color: 'grey.400', mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Stack direction="row" gap={1} flexWrap="wrap">
          <StatChip
            icon={<BoltIcon sx={{ fontSize: 16, color: 'green.main' }} />}
            label={t('mapPage.available', { available, total })}
          />
          {hours && (
            <StatChip
              icon={<AccessTimeIcon sx={{ fontSize: 16, color: 'grey.300' }} />}
              label={hours}
            />
          )}
        </Stack>

        {station.connectors && station.connectors.length > 0 && (
          <Stack
            sx={{
              gap: 1,
              overflowY: 'auto',
              maxHeight: isDesktop ? 'none' : 220,
              minHeight: 0,
            }}
          >
            {station.connectors.map((connector) => (
              <Stack
                key={connector.connectorId}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: '12px',
                  backgroundColor: 'grey.900',
                  border: '1px solid',
                  borderColor: 'grey.800',
                  gap: 1,
                }}
              >
                <Stack sx={{ minWidth: 0, gap: 0.25 }}>
                  <Typography
                    sx={{
                      color: 'common.white',
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    <Box component="span">#{connector.connectorId}</Box>
                    {connector.type ? ` · ${connector.type}` : ''}
                  </Typography>
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: getConnectorStatusColor(
                          connector.status,
                        ),
                      }}
                    />
                    <Typography sx={{ color: 'grey.400', fontSize: 12 }}>
                      {t(`statuses.connector.${connector.status}`)}
                    </Typography>
                  </Stack>
                </Stack>
                {hasPricing && (
                  <Stack sx={{ alignItems: 'flex-end', flexShrink: 0 }}>
                    <Typography
                      sx={{
                        color: 'common.white',
                        fontSize: 14,
                        fontWeight: 700,
                      }}
                    >
                      ₴ {station.price}
                      <Box
                        component="span"
                        sx={{ color: 'grey.500', fontSize: 11, fontWeight: 500 }}
                      >
                        {' '}
                        {t('mapPage.perKwh')}
                      </Box>
                    </Typography>
                    {station.downtimePricePerMinute != null && (
                      <Typography sx={{ color: 'grey.400', fontSize: 12 }}>
                        ₴ {station.downtimePricePerMinute}
                        <Box
                          component="span"
                          sx={{ color: 'grey.500', fontSize: 11 }}
                        >
                          {' '}
                          {t('mapPage.perIdleMin')}
                        </Box>
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
            ))}
          </Stack>
        )}

        {hasPricing && station.downtimeFreeMinutes != null && (
          <Typography sx={{ color: 'grey.500', fontSize: 11 }}>
            {t('mapPage.freeIdleMinutes', {
              minutes: station.downtimeFreeMinutes,
            })}
          </Typography>
        )}

        <Stack direction="row" gap={1} sx={{ mt: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={<DirectionsIcon />}
            href={navigateUrl}
            target="_blank"
            rel="noreferrer"
            sx={{
              flex: 1,
              borderColor: 'grey.700',
              color: 'common.white',
              borderRadius: '12px',
              py: 1.25,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                borderColor: 'green.main',
                backgroundColor: 'rgba(29,239,131,0.08)',
              },
            }}
          >
            {t('mapPage.navigate')}
          </Button>
          <Button
            variant="contained"
            onClick={() => onGoToStation(station)}
            sx={{
              flex: 1,
              backgroundColor: 'green.main',
              color: 'common.white',
              borderRadius: '12px',
              py: 1.25,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { backgroundColor: 'green.dark', boxShadow: 'none' },
            }}
          >
            {t('mapPage.goToStation')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

const StatChip = ({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={0.75}
    sx={{
      px: 1.25,
      py: 0.5,
      borderRadius: '8px',
      backgroundColor: 'grey.900',
      border: '1px solid',
      borderColor: 'grey.800',
    }}
  >
    {icon}
    <Typography
      variant="caption"
      sx={{ color: 'common.white', fontWeight: 500 }}
    >
      {label}
    </Typography>
  </Stack>
);
